import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import { updateNotificationRead } from '../../ducks/Notifications/actions';
import notificationService from '../../services/notificationService';
import ThemeToggle from '../ThemeToggle';
import ChangelogIcon from '../ChangelogIcon';
import FeedbackIcon from '../FeedbackIcon';
import { IcoBell, IcoChevR, IcoMenu, IcoX } from './icons';

/**
 * Top bar for the new app shell. Replaces src/components/PageHeader for the
 * dashboard surface. Preserves every behavior the old header had:
 *   - Notifications: load list + unread count, mark-read, mark-all-read,
 *     delete, click-to-navigate (supports old `url` and new `data.clickAction`
 *     / `data.notificationUrl` shapes)
 *   - Theme toggle (light / dark / system)
 *   - Changelog icon
 *
 * Differences vs PageHeader:
 *   - No avatar dropdown here — moved into the sidebar's user-card.
 *   - Crumbs derived from URL instead of an antd-managed page title.
 *   - Hidden outside `/dashboard` (assessment, share, marketing render
 *     their own chrome and live outside the shell).
 */

// Path → human-readable label. Order matters: longest match wins.
const CRUMB_MAP = [
  ['/dashboard/home', 'Home'],
  ['/dashboard/your-week', 'Home', 'Your week'],
  ['/dashboard/workout/workout-plans', 'Workout', 'Plans'],
  ['/dashboard/workout/history', 'Workout', 'History'],
  ['/dashboard/workout', 'Workout'],
  ['/dashboard/nutrition/meal-plan', 'Nutrition', 'Meal plan'],
  ['/dashboard/nutrition/recipes', 'Nutrition', 'Recipes'],
  ['/dashboard/nutrition/shopping-list', 'Nutrition', 'Shopping list'],
  ['/dashboard/nutrition/preferences', 'Nutrition', 'Preferences'],
  ['/dashboard/nutrition', 'Nutrition'],
  ['/dashboard/stats/progress', 'Progress'],
  ['/dashboard/stats', 'Progress'],
  ['/dashboard/tools/macro-calculator', 'Tools', 'Macro calculator'],
  ['/dashboard/tools', 'Tools'],
  ['/dashboard/admin/clients', 'Clients'],
  ['/dashboard/admin/trainers', 'Trainers'],
  ['/dashboard/admin/library/workout-programs', 'Library', 'Workout programs'],
  ['/dashboard/admin/library/workouts', 'Library', 'Workouts'],
  ['/dashboard/admin/library/meal-plans', 'Library', 'Meal plans'],
  ['/dashboard/admin/library', 'Library'],
  ['/dashboard/admin/assessments', 'Assessments'],
  ['/dashboard/admin/challenges', 'Challenges'],
  // The coach's Home (the sidebar calls it Home too).
  ['/dashboard/admin', 'Home'],
  ['/dashboard/messages', 'Messages'],
  ['/dashboard/settings/business', 'Settings', 'Business'],
  ['/dashboard/settings/coaching', 'Settings', 'Coaching'],
  ['/dashboard/settings/integrations', 'Settings', 'Integrations'],
  ['/dashboard/settings/platform', 'Settings', 'Platform'],
  ['/dashboard/settings/billing', 'Settings', 'Billing'],
  ['/dashboard/settings', 'Settings'],
  ['/dashboard/profile', 'Profile'],
  ['/dashboard', 'Dashboard'],
];

function deriveCrumbs(pathname) {
  for (const entry of CRUMB_MAP) {
    const [prefix, ...labels] = entry;
    if (pathname === prefix || pathname.startsWith(prefix + '/')) {
      return labels;
    }
  }
  return [];
}

class Topbar extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      notifOpen: false,
      notifications: [],
      unreadCount: 0,
      loading: false,
    };
    this.panelRef = null;
    this.bellRef = null;
    this.handleDocClick = this.handleDocClick.bind(this);
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleDocClick);
    if (this.props.auth?.user) {
      this.loadNotifications();
      this.loadUnreadCount();
    }
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleDocClick);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.auth?.user !== this.props.auth?.user && this.props.auth?.user) {
      this.loadNotifications();
      this.loadUnreadCount();
    }
  }

  handleDocClick(e) {
    if (!this.state.notifOpen) return;
    const inPanel = this.panelRef && this.panelRef.contains(e.target);
    const inBell = this.bellRef && this.bellRef.contains(e.target);
    if (!inPanel && !inBell) {
      this.setState({ notifOpen: false });
    }
  }

  loadNotifications = async () => {
    this.setState({ loading: true });
    try {
      const response = await notificationService.getNotifications({ limit: 20 });
      const list = response?.notifications || [];
      this.setState({ notifications: list });
      this.props.setNotifications?.(list);
    } catch (err) {
      console.error('Error loading notifications:', err);
    } finally {
      this.setState({ loading: false });
    }
  };

  loadUnreadCount = async () => {
    try {
      const count = await notificationService.getUnreadCount();
      this.setState({ unreadCount: count || 0 });
    } catch (err) {
      console.error('Error loading unread count:', err);
    }
  };

  markAsRead = async (n) => {
    try {
      await notificationService.markAsRead(n._id);
      this.props.markRead?.(n._id);
      this.setState((s) => ({
        notifications: s.notifications.map((x) =>
          x._id === n._id ? { ...x, readAt: new Date().toISOString() } : x
        ),
        unreadCount: Math.max(0, s.unreadCount - 1),
      }));
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  markAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      await this.loadNotifications();
      await this.loadUnreadCount();
    } catch (err) {
      console.error('Error marking all as read:', err);
    }
  };

  // Per-row delete (X button on each notification). Optimistic local
  // splice so the row vanishes immediately; on failure, refetch to
  // resync state with the server.
  deleteNotification = async (n, e) => {
    if (e) e.stopPropagation();
    if (!n?._id) return;
    const wasUnread = this.isUnread(n);
    this.setState((s) => ({
      notifications: s.notifications.filter((x) => x._id !== n._id),
      unreadCount: wasUnread
        ? Math.max(0, s.unreadCount - 1)
        : s.unreadCount,
    }));
    try {
      await notificationService.deleteNotification(n._id);
    } catch (err) {
      console.error('Error deleting notification:', err);
      // Roll back by reloading from the server.
      await this.loadNotifications();
      await this.loadUnreadCount();
    }
  };

  handleNotifClick = (n) => {
    this.markAsRead(n);
    const clickUrl =
      n.data?.clickAction || n.data?.notificationUrl || n.url;
    if (!clickUrl) return;
    if (clickUrl.startsWith('http://') || clickUrl.startsWith('https://')) {
      window.open(clickUrl, '_blank');
    } else {
      const parts = clickUrl.split(`${process.env.REACT_APP_APP_URL}`);
      this.props.history?.push(parts[parts.length - 1] || clickUrl);
    }
    this.setState({ notifOpen: false });
  };

  isUnread = (n) => !n.readAt && n.status !== 'read';

  shouldRender() {
    const path = this.props.location?.pathname || '';
    if (!path.startsWith('/dashboard')) return false;
    return !!this.props.auth?.isAuthenticated;
  }

  renderNotifPanel() {
    const { notifications, loading, unreadCount } = this.state;
    return (
      <div
        ref={(el) => {
          this.panelRef = el;
        }}
        style={{
          position: 'absolute',
          top: 44,
          right: 0,
          width: 360,
          maxHeight: 480,
          background: 'var(--bg-surface)',
          border: '1px solid var(--border)',
          borderRadius: 12,
          boxShadow: 'var(--shadow-pop)',
          zIndex: 30,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 14px',
            borderBottom: '1px solid var(--border-soft)',
          }}
        >
          <div style={{ fontWeight: 600, color: 'var(--fg)' }}>Notifications</div>
          {unreadCount > 0 ? (
            <button
              onClick={this.markAllAsRead}
              style={{
                background: 'transparent',
                border: 0,
                color: 'var(--primary)',
                fontSize: 12,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              Mark all as read
            </button>
          ) : null}
        </div>
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {loading && notifications.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--fg-muted)', fontSize: 13 }}>
              Loading…
            </div>
          ) : notifications.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--fg-muted)', fontSize: 13 }}>
              You don't have any notifications.
            </div>
          ) : (
            notifications.map((n) => {
              const unread = this.isUnread(n);
              return (
                <div
                  key={n._id || n.id}
                  onClick={() => this.handleNotifClick(n)}
                  style={{
                    padding: '12px 14px',
                    borderBottom: '1px solid var(--border-soft)',
                    cursor: 'pointer',
                    background: unread ? 'var(--primary-soft)' : 'transparent',
                    display: 'flex',
                    gap: 10,
                  }}
                >
                  <div
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      marginTop: 6,
                      background: unread ? 'var(--primary)' : 'transparent',
                      flexShrink: 0,
                    }}
                  />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 8,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: 'var(--fg)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {n.title}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--fg-subtle)', flexShrink: 0 }}>
                        {n.createdAt ? moment(n.createdAt).fromNow() : ''}
                      </div>
                    </div>
                    {n.message ? (
                      <div
                        style={{
                          fontSize: 12,
                          color: 'var(--fg-muted)',
                          marginTop: 2,
                          lineHeight: 1.4,
                        }}
                      >
                        {n.message}
                      </div>
                    ) : null}
                  </div>
                  {/* Per-row delete. Stops propagation so the row's
                      "click to mark read + navigate" handler doesn't
                      also fire when the user is just dismissing. */}
                  <button
                    type="button"
                    onClick={(e) => this.deleteNotification(n, e)}
                    aria-label="Delete notification"
                    title="Delete"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 24,
                      height: 24,
                      borderRadius: 6,
                      border: 'none',
                      background: 'transparent',
                      color: 'var(--fg-subtle)',
                      cursor: 'pointer',
                      flexShrink: 0,
                      alignSelf: 'flex-start',
                      marginTop: 2,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'var(--bg-elev)';
                      e.currentTarget.style.color = 'var(--fg)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = 'var(--fg-subtle)';
                    }}
                  >
                    <IcoX size={12} />
                  </button>
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  }

  render() {
    if (!this.shouldRender()) return null;

    const { location } = this.props;
    // Route-derived crumbs + any dynamic segments a page pushed (e.g. the
    // challenge name on the landing builder → "Admin › Landing page — …").
    const extra = Array.isArray(this.props.pageCrumbs) ? this.props.pageCrumbs : [];
    const crumbs = [...deriveCrumbs(location?.pathname || ''), ...extra];
    const { notifOpen, unreadCount } = this.state;

    return (
      <div className="topbar">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          {/* Mobile-only hamburger. CSS hides this on desktop via the
              `.menu-toggle` rule in theme.css's media queries. */}
          <button
            type="button"
            className="icon-btn menu-toggle"
            aria-label="Open navigation menu"
            onClick={() => this.props.onOpenMobileNav?.()}
          >
            <IcoMenu size={18} />
          </button>
          {/* `marginBottom: 0` overrides the .crumbs page-strip default
              — inside the topbar chrome we want the row vertically
              centered, not pushed down. */}
          <div className="crumbs" style={{ minWidth: 0, marginBottom: 0 }}>
          {crumbs.length === 0 ? (
            <span className="here">Dashboard</span>
          ) : (
            crumbs.map((label, i) => (
              <React.Fragment key={i}>
                {i > 0 ? <IcoChevR size={14} /> : null}
                <span className={i === crumbs.length - 1 ? 'here' : ''}>{label}</span>
              </React.Fragment>
            ))
          )}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* Search / command-palette removed — was a placeholder
              (`aria-label="Search (coming soon)"`) with no handler.
              Bring back when a real palette / scoped search ships. */}

          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <FeedbackIcon />
            <ChangelogIcon />
          </div>

          <ThemeToggle />

          <div style={{ position: 'relative' }}>
            <button
              ref={(el) => {
                this.bellRef = el;
              }}
              className="icon-btn"
              aria-label="Notifications"
              onClick={() => this.setState((s) => ({ notifOpen: !s.notifOpen }))}
            >
              <IcoBell size={18} />
              {unreadCount > 0 ? <span className="pulse" /> : null}
            </button>
            {notifOpen ? this.renderNotifPanel() : null}
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  auth: state.auth,
  userNotifications: state.notifications.userNotifications,
  pageCrumbs: state.global?.pageCrumbs || null,
});

const mapDispatchToProps = {
  setNotifications: (notifications) => ({
    type: 'GET_ALL_NOTIFICATIONS_SUCCESS',
    payload: notifications,
  }),
  markRead: updateNotificationRead,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Topbar));
